<template>
  <div>
    <!-- ==================== NAVBAR MÓVIL ==================== -->
    <nav class="mobile-navbar d-lg-none shadow-sm">
      <div class="container-fluid mobile-navbar-inner h-100">
        <div class="mobile-logo-bg">
          <img src="/favicon.png" alt="Divisan" class="mobile-logo" />
        </div>

        <span class="mobile-page-title">{{ pageTitle }}</span>

        <button
          class="hamburger-btn"
          type="button"
          @click="openMobileMenu"
          aria-label="Abrir menú"
          :aria-expanded="isMobileMenuOpen"
        >
          <i class="bi bi-list"></i>
        </button>
      </div>
    </nav>

    <!-- ==================== MENÚ MÓVIL / TABLET (drawer) ==================== -->
    <transition name="mobile-backdrop-fade">
      <div
        v-if="isMobileMenuOpen"
        class="mobile-backdrop d-lg-none"
        @click="closeMobileMenu"
      ></div>
    </transition>

    <transition name="mobile-drawer-slide">
      <aside v-if="isMobileMenuOpen" class="mobile-drawer d-lg-none">
        <div class="mobile-drawer-header">
          <div class="logo-wrapper">
            <img
              src="@/assets/img/Divisan/logo_divisan.png"
              alt="Logo"
              class="logo-img"
            />
          </div>
          <button
            class="drawer-close-btn"
            @click="closeMobileMenu"
            aria-label="Cerrar menú"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <nav class="mobile-drawer-menu">
          <div class="menu-section" v-for="section in visibleSections" :key="section.key">
            <button
              type="button"
              class="section-header"
              @click="toggleSection(section.key)"
              :aria-expanded="isSectionOpen(section.key)"
            >
              <span class="section-title">{{ section.label }}</span>
              <i
                class="bi bi-chevron-down section-chevron"
                :class="{ rotated: isSectionOpen(section.key) }"
              ></i>
            </button>

            <div
              class="section-items-wrapper"
              :class="{ 'is-open': isSectionOpen(section.key) }"
            >
              <div class="section-items-inner">
                <template v-for="item in section.items" :key="item.tab">
                  <!-- Item con sub-catálogos (acordeón anidado) -->
                  <template v-if="item.subItems">
                    <button
                      type="button"
                      @click="toggleGroup(item.tab)"
                      :class="['menu-item', 'menu-item-group', { active: isGroupActive(item) }]"
                      :aria-expanded="isGroupOpen(item.tab)"
                    >
                      <i :class="['bi', item.icon]"></i>
                      <span>{{ item.label }}</span>
                      <i
                        class="bi bi-chevron-down group-chevron"
                        :class="{ rotated: isGroupOpen(item.tab) }"
                      ></i>
                    </button>
                    <div class="submenu-wrapper" :class="{ 'is-open': isGroupOpen(item.tab) }">
                      <div class="submenu-inner">
                        <button
                          v-for="sub in item.subItems"
                          :key="sub.tab"
                          @click="selectTabMobile(sub.tab)"
                          :class="['menu-item', 'menu-item-sub', { active: activeTab === sub.tab }]"
                        >
                          <i :class="['bi', sub.icon]"></i>
                          <span>{{ sub.label }}</span>
                        </button>
                      </div>
                    </div>
                  </template>

                  <!-- Item simple -->
                  <button
                    v-else
                    @click="selectTabMobile(item.tab)"
                    :class="['menu-item', { active: activeTab === item.tab }]"
                  >
                    <i :class="['bi', item.icon]"></i>
                    <span>{{ item.label }}</span>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </nav>

        <div class="mobile-drawer-footer">
          <div class="user-card" v-if="userName">
            <div class="user-details">
              <span class="user-name">{{ userName }}</span>
              <span class="user-role">{{ roleLabel }}</span>
            </div>
            <button
              @click="handleMobileLogout"
              class="user-logout-btn"
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
            >
              <i class="bi bi-box-arrow-right"></i>
            </button>
          </div>
          <button v-else @click="handleMobileLogout" class="menu-item logout">
            <i class="bi bi-power"></i>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>
    </transition>

    <!-- ==================== SIDEBAR ESCRITORIO ==================== -->
    <aside :class="['sidebar d-none d-lg-flex', { collapsed: isCollapsed }]">
      <div class="sidebar-header">
        <div class="logo-wrapper" v-show="!isCollapsed">
          <img src="@/assets/img/Divisan/logo_divisan.png" alt="Logo" class="logo-img" />
        </div>
        <button class="toggle-btn" @click="toggleSidebar">
          <i
            :class="isCollapsed ? 'bi bi-text-indent-left' : 'bi bi-text-indent-right'"
          ></i>
        </button>
      </div>

      <!-- Riel colapsado: un ícono por sección, con flyout al hacer click -->
      <nav v-if="isCollapsed" class="sidebar-rail">
        <button
          v-for="section in visibleSections"
          :key="section.key"
          type="button"
          :class="['rail-btn', { active: isSectionActive(section), open: activeFlyout?.key === section.key }]"
          :title="section.label"
          :aria-expanded="activeFlyout?.key === section.key"
          @click="toggleFlyout(section, $event)"
        >
          <i :class="['bi', section.icon || 'bi-dot']"></i>
        </button>
      </nav>

      <!-- Expandido: acordeón normal -->
      <nav v-else class="sidebar-menu">
        <div class="menu-section" v-for="section in visibleSections" :key="section.key">
          <button
            type="button"
            class="section-header"
            @click="toggleSection(section.key)"
            :aria-expanded="isSectionOpen(section.key)"
          >
            <span class="section-title">{{ section.label }}</span>
            <i
              class="bi bi-chevron-down section-chevron"
              :class="{ rotated: isSectionOpen(section.key) }"
            ></i>
          </button>

          <div class="section-items-wrapper" :class="{ 'is-open': isSectionOpen(section.key) }">
            <div class="section-items-inner">
              <template v-for="item in section.items" :key="item.tab">
                <!-- Item con sub-catálogos (acordeón anidado) -->
                <template v-if="item.subItems">
                  <button
                    type="button"
                    @click="toggleGroup(item.tab)"
                    :class="['menu-item', 'menu-item-group', { active: isGroupActive(item) }]"
                    :aria-expanded="isGroupOpen(item.tab)"
                  >
                    <i :class="['bi', item.icon]"></i>
                    <span>{{ item.label }}</span>
                    <i
                      class="bi bi-chevron-down group-chevron"
                      :class="{ rotated: isGroupOpen(item.tab) }"
                    ></i>
                  </button>
                  <div class="submenu-wrapper" :class="{ 'is-open': isGroupOpen(item.tab) }">
                    <div class="submenu-inner">
                      <button
                        v-for="sub in item.subItems"
                        :key="sub.tab"
                        @click="selectTab(sub.tab)"
                        :class="['menu-item', 'menu-item-sub', { active: activeTab === sub.tab }]"
                      >
                        <i :class="['bi', sub.icon]"></i>
                        <span>{{ sub.label }}</span>
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Item simple -->
                <button
                  v-else
                  @click="selectTab(item.tab)"
                  :class="['menu-item', { active: activeTab === item.tab }]"
                >
                  <i :class="['bi', item.icon]"></i>
                  <span>{{ item.label }}</span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </nav>

      <!-- Flyout flotante de la sección activa en modo colapsado -->
      <Teleport to="body">
        <transition name="flyout-fade">
          <div
            v-if="activeFlyout"
            class="sidebar-flyout"
            :style="{ top: activeFlyout.top + 'px', left: activeFlyout.left + 'px' }"
          >
            <div class="flyout-header">{{ activeFlyout.label }}</div>
            <div class="flyout-items">
              <template v-for="item in activeFlyout.items" :key="item.tab">
                <template v-if="item.subItems">
                  <button
                    type="button"
                    :class="['flyout-item', 'flyout-item-group', { active: isGroupActive(item) }]"
                    @click="toggleGroup(item.tab)"
                    :aria-expanded="isGroupOpen(item.tab)"
                  >
                    <i :class="['bi', item.icon]"></i>
                    <span>{{ item.label }}</span>
                    <i
                      class="bi bi-chevron-down flyout-chevron"
                      :class="{ rotated: isGroupOpen(item.tab) }"
                    ></i>
                  </button>
                  <div class="flyout-subwrapper" :class="{ 'is-open': isGroupOpen(item.tab) }">
                    <div class="flyout-subinner">
                      <button
                        v-for="sub in item.subItems"
                        :key="sub.tab"
                        :class="['flyout-item', 'flyout-item-sub', { active: activeTab === sub.tab }]"
                        @click="selectFlyoutTab(sub.tab)"
                      >
                        <i :class="['bi', sub.icon]"></i>
                        <span>{{ sub.label }}</span>
                      </button>
                    </div>
                  </div>
                </template>

                <button
                  v-else
                  :class="['flyout-item', { active: activeTab === item.tab }]"
                  @click="selectFlyoutTab(item.tab)"
                >
                  <i :class="['bi', item.icon]"></i>
                  <span>{{ item.label }}</span>
                </button>
              </template>
            </div>
          </div>
        </transition>
      </Teleport>

      <div class="sidebar-footer">
        <div class="user-card" v-if="!isCollapsed && userName">
          <div class="user-avatar">
            <i class="bi bi-person-circle"></i>
          </div>
          <div class="user-details">
            <span class="user-name">{{ userName }}</span>
            <span class="user-role">{{ roleLabel }}</span>
          </div>
          <button
            @click="emit('logout')"
            class="user-logout-btn"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </div>
        <button
          v-else
          @click="emit('logout')"
          class="menu-item logout"
          :title="isCollapsed ? 'Cerrar sesión' : ''"
        >
          <i class="bi bi-power"></i>
          <span v-show="!isCollapsed">Cerrar sesión</span>
        </button>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { NAV_CONFIG } from "@/config/navigation";

// ── Props & Emits ──────────────────────────────────────────
const props = defineProps({
  activeTab: { type: String, required: true },
  userRole: { type: String, default: "user" },
  userName: { type: String, default: "" },
  pageTitle: { type: String, default: "" },
});

const emit = defineEmits(["update:activeTab", "toggle", "logout"]);

// ── Estado ─────────────────────────────────────────────────
const isCollapsed = ref(false);
const isMobileMenuOpen = ref(false);

// Flyout de sección (rail colapsado): popover flotante teleportado a
// <body>, anclado junto al botón de la sección que se haya clickeado.
const activeFlyout = ref(null);

// Secciones expandidas (acordeón de categorías). Por defecto solo
// se abre la sección que contiene el tab activo, para no saturar
// la vista con tantas categorías.
function findSectionKey(tab) {
  const section = NAV_CONFIG.sections.find((s) =>
    s.items.some((i) => i.tab === tab || i.subItems?.some((sub) => sub.tab === tab))
  );
  return section ? section.key : null;
}

// Encuentra el item-grupo (con subItems) dueño de un tab, para saber qué
// submenú anidado debe abrirse cuando el tab activo es uno de sus hijos.
function findGroupTab(tab) {
  for (const section of NAV_CONFIG.sections) {
    const group = section.items.find((i) => i.subItems?.some((sub) => sub.tab === tab));
    if (group) return group.tab;
  }
  return null;
}

// Acordeón de una sola sección: abrir una cierra la que estuviera abierta,
// en vez de ir acumulando secciones expandidas. Al arrancar solo se abre la
// sección del tab activo.
const openSectionKey = ref(findSectionKey(props.activeTab));

// Acordeón anidado de sub-catálogos (independiente del de secciones). Al
// arrancar se abre el grupo dueño del tab activo, si aplica.
const openGroupKey = ref(findGroupTab(props.activeTab));

// ── Computed ───────────────────────────────────────────────
// Un item puede restringirse a ciertos roles dentro de una sección a la que
// varios roles tienen acceso (p. ej. "Inyección" trae un tab para Nico y
// otro para Brenda). Sin `roles` el item es visible para cualquiera que
// tenga acceso a la sección, igual que antes.
function itemVisible(item) {
  return !item.roles || item.roles.includes(props.userRole);
}

const visibleSections = computed(() => {
  const allowed = NAV_CONFIG.roleAccess[props.userRole] ?? [];
  return NAV_CONFIG.sections
    .filter((s) => allowed.includes(s.key))
    .map((s) => ({ ...s, items: s.items.filter(itemVisible) }));
});

const roleLabel = computed(() => NAV_CONFIG.roleLabels[props.userRole] ?? props.userRole);

// ── Métodos ────────────────────────────────────────────────
function toggleSidebar() {
  isCollapsed.value = !isCollapsed.value;
  if (!isCollapsed.value) closeFlyout();
  emit("toggle", isCollapsed.value);
}

// ── Flyout de sección (rail colapsado) ───────────────────────
function isSectionActive(section) {
  return section.items.some(
    (item) => item.tab === props.activeTab || item.subItems?.some((sub) => sub.tab === props.activeTab)
  );
}

function toggleFlyout(section, evt) {
  if (activeFlyout.value?.key === section.key) {
    closeFlyout();
    return;
  }
  const btn = evt.currentTarget;
  const rect = btn.getBoundingClientRect();
  const gap = 10;
  const estimatedHeight = 60 + section.items.length * 46;
  const top = Math.min(rect.top, window.innerHeight - estimatedHeight - 16);
  activeFlyout.value = {
    key: section.key,
    label: section.label,
    items: section.items,
    top: Math.max(16, top),
    left: rect.right + gap,
  };
}

function closeFlyout() {
  activeFlyout.value = null;
}

function selectFlyoutTab(tab) {
  selectTab(tab);
  closeFlyout();
}

function handleFlyoutOutsideClick(e) {
  if (!activeFlyout.value) return;
  if (e.target.closest(".sidebar-flyout") || e.target.closest(".rail-btn")) return;
  closeFlyout();
}

function handleFlyoutViewportChange() {
  closeFlyout();
}

// ── Menú móvil / tablet (drawer) ─────────────────────────────
function openMobileMenu() {
  isMobileMenuOpen.value = true;
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false;
}

function selectTabMobile(tab) {
  selectTab(tab);
  closeMobileMenu();
}

function handleMobileLogout() {
  closeMobileMenu();
  emit("logout");
}

function handleEscape(e) {
  if (e.key === "Escape" && isMobileMenuOpen.value) closeMobileMenu();
}

watch(isMobileMenuOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
  if (open) {
    window.addEventListener("keydown", handleEscape);
  } else {
    window.removeEventListener("keydown", handleEscape);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscape);
  document.body.style.overflow = "";
});

document.addEventListener("mousedown", handleFlyoutOutsideClick);
window.addEventListener("resize", handleFlyoutViewportChange);

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleFlyoutOutsideClick);
  window.removeEventListener("resize", handleFlyoutViewportChange);
});

function isSectionOpen(key) {
  return openSectionKey.value === key;
}

function toggleSection(key) {
  openSectionKey.value = openSectionKey.value === key ? null : key;
}

function isGroupOpen(tab) {
  return openGroupKey.value === tab;
}

function toggleGroup(tab) {
  openGroupKey.value = openGroupKey.value === tab ? null : tab;
}

// Resalta el encabezado del grupo cuando el tab activo es uno de sus hijos.
function isGroupActive(item) {
  return item.subItems?.some((sub) => sub.tab === props.activeTab) ?? false;
}

function selectTab(tab) {
  const sectionKey = findSectionKey(tab);
  if (sectionKey) openSectionKey.value = sectionKey;
  const groupTab = findGroupTab(tab);
  if (groupTab) openGroupKey.value = groupTab;
  emit("update:activeTab", tab);
}

// Si el tab activo cambia desde afuera (navegación externa, deep link),
// su sección y su grupo (si aplica) deben quedar expandidos.
watch(
  () => props.activeTab,
  (tab) => {
    const sectionKey = findSectionKey(tab);
    if (sectionKey) openSectionKey.value = sectionKey;
    const groupTab = findGroupTab(tab);
    if (groupTab) openGroupKey.value = groupTab;
  }
);
</script>

<style scoped>
/* ======================== NAVBAR MÓVIL ======================== */
.mobile-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 65px;
  background-color: var(--d-cream);
  border-bottom: 2px solid var(--d-green);
  z-index: 1100;
}

.mobile-navbar-inner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 1rem;
}

.mobile-logo-bg {
  width: 46px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: var(--d-radius-md);
  border: 1px solid var(--d-green-dark);
  justify-self: start;
}

.mobile-logo {
  height: 24px;
  width: 35px;
}

.mobile-page-title {
  justify-self: center;
  text-align: center;
  color: var(--d-brown);
  font-weight: 700;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 45vw;
}

.hamburger-btn {
  justify-self: end;
  background: transparent;
  border: none;
  border-radius: 10px;
  color: var(--d-brown);
  font-size: 1.8rem;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.hamburger-btn:hover,
.hamburger-btn:active {
  background: var(--d-nav-hover-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
}

/* ======================== MENÚ MÓVIL / TABLET (drawer) ======================== */
.mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 40, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1150;
}

.mobile-drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: min(85vw, 340px);
  height: 100vh;
  height: 100dvh;
  background-color: var(--d-cream);
  border-right: 2px solid var(--d-green);
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.25);
  z-index: 1200;
  display: flex;
  flex-direction: column;
}

.mobile-drawer-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--d-border);
  flex-shrink: 0;
}

.drawer-close-btn {
  background: transparent;
  border: none;
  border-radius: 10px;
  color: var(--d-brown);
  font-size: 1.1rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.drawer-close-btn:hover {
  background: var(--d-nav-hover-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
}

.mobile-drawer-menu {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0 1rem;
}

.mobile-drawer-footer {
  padding: 1rem;
  border-top: 1px solid var(--d-border);
  flex-shrink: 0;
}

/* Transiciones */
.mobile-backdrop-fade-enter-active,
.mobile-backdrop-fade-leave-active {
  transition: opacity 0.25s ease;
}
.mobile-backdrop-fade-enter-from,
.mobile-backdrop-fade-leave-to {
  opacity: 0;
}

.mobile-drawer-slide-enter-active,
.mobile-drawer-slide-leave-active {
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.mobile-drawer-slide-enter-from,
.mobile-drawer-slide-leave-to {
  transform: translateX(-100%);
}

@media (prefers-reduced-motion: reduce) {
  .mobile-backdrop-fade-enter-active,
  .mobile-backdrop-fade-leave-active,
  .mobile-drawer-slide-enter-active,
  .mobile-drawer-slide-leave-active {
    transition: none;
  }
}

/* ======================== SIDEBAR ESCRITORIO ======================== */
.sidebar {
  width: 260px;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background-color: var(--d-cream);
  border-right: 2px solid var(--d-green);
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1050;
}

.sidebar.collapsed {
  width: 80px;
}

.sidebar-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
  border-bottom: 1px solid var(--d-border);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  justify-content: center;
  padding: 1.5rem 0;
}

.logo-wrapper {
  background: white;
  padding: 5px;
  border-radius: 8px;
  border: 1px solid rgba(40, 54, 24, 0.2);
}

.logo-img {
  height: 32px;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: var(--d-brown);
  font-size: 1.5rem;
  cursor: pointer;
}

/* ======================== MENÚ ======================== */
.sidebar-menu {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
}

.menu-section {
  margin-bottom: 0.4rem;
  padding: 0 0.8rem;
}

/* ── Encabezado de sección (acordeón) ── */
.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem 0.4rem 0.5rem 12px;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.section-header:hover {
  background: var(--d-nav-hover-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
}

.section-header:hover .section-title {
  color: var(--d-green-dark);
}

.section-title {
  color: var(--d-brown);
  font-weight: 800;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
  transition: color 0.2s ease;
}

.section-chevron {
  font-size: 0.75rem;
  color: var(--d-green);
  opacity: 0.75;
  transition: transform 0.25s ease;
}

.section-chevron.rotated {
  transform: rotate(180deg);
}

/* ── Contenido colapsable de cada sección ── */
.section-items-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}

.section-items-wrapper.is-open {
  grid-template-rows: 1fr;
}

.section-items-inner {
  overflow: hidden;
  min-height: 0;
  padding-top: 0.2rem;
}

.menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  color: var(--d-green-dark);
  background: transparent;
  border: none;
  border-radius: 10px;
  transition: all 0.2s;
  margin-bottom: 4px;
  cursor: pointer;
  font-weight: 500;
}

.menu-item i {
  font-size: 1.2rem;
  min-width: 35px;
  color: var(--d-green);
}

.menu-item:hover {
  background: var(--d-nav-hover-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
  color: var(--d-brown);
}
.menu-item.active {
  background: var(--d-nav-active-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
  box-shadow: inset 0 0 0 1px var(--d-nav-active-brd);
  color: var(--d-green-dark);
  font-weight: 700;
}
.menu-item.active i {
  color: var(--d-green-dark);
}

/* ── Item con sub-catálogos (grupo con flecha) ── */
.menu-item-group {
  justify-content: flex-start;
  gap: 0;
}
.menu-item i.group-chevron {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--d-green);
  opacity: 0.75;
  transition: transform 0.25s ease;
  min-width: 0;
}
.group-chevron.rotated {
  transform: rotate(180deg);
}

.submenu-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}
.submenu-wrapper.is-open {
  grid-template-rows: 1fr;
}
.submenu-inner {
  overflow: hidden;
  min-height: 0;
}

.menu-item-sub {
  padding-left: 12px;
}
.menu-item-sub i {
  font-size: 1rem;
  min-width: 28px;
}

.logout:hover {
  background: rgba(160, 34, 8, 0.1);
  color: #d32f2f;
}

/* ======================== RIEL COLAPSADO ======================== */
.sidebar-rail {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0;
  overflow-y: auto;
}

.rail-btn {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--d-radius-md);
  color: var(--d-green);
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.rail-btn:hover {
  background: var(--d-nav-hover-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
  color: var(--d-brown);
}

.rail-btn.active,
.rail-btn.open {
  background: var(--d-nav-active-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
  box-shadow: inset 0 0 0 1px var(--d-nav-active-brd);
  color: var(--d-green-dark);
}

/* ======================== FLYOUT (rail colapsado) ======================== */
.sidebar-flyout {
  position: fixed;
  min-width: 220px;
  max-width: 280px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: var(--d-cream);
  border: 1px solid var(--d-border);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-3);
  padding: 0.5rem;
  z-index: 1300;
}

.flyout-header {
  color: var(--d-brown);
  font-weight: 800;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
  padding: 0.4rem 0.6rem 0.5rem;
}

.flyout-items {
  display: flex;
  flex-direction: column;
}

.flyout-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0;
  color: var(--d-green-dark);
  background: transparent;
  border: none;
  border-radius: 10px;
  padding: 0.55rem 0.6rem;
  margin-bottom: 2px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.flyout-item i {
  font-size: 1.1rem;
  min-width: 32px;
  color: var(--d-green);
}

.flyout-item:hover {
  background: var(--d-nav-hover-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
  color: var(--d-brown);
}

.flyout-item.active {
  background: var(--d-nav-active-bg);
  backdrop-filter: var(--d-nav-blur);
  -webkit-backdrop-filter: var(--d-nav-blur);
  box-shadow: inset 0 0 0 1px var(--d-nav-active-brd);
  color: var(--d-green-dark);
  font-weight: 700;
}

.flyout-item.active i {
  color: var(--d-green-dark);
}

.flyout-item-group {
  justify-content: flex-start;
}

.flyout-chevron {
  margin-left: auto;
  font-size: 0.7rem;
  color: var(--d-green);
  opacity: 0.75;
  transition: transform 0.25s ease;
  min-width: 0;
}

.flyout-chevron.rotated {
  transform: rotate(180deg);
}

.flyout-subwrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}

.flyout-subwrapper.is-open {
  grid-template-rows: 1fr;
}

.flyout-subinner {
  overflow: hidden;
  min-height: 0;
}

.flyout-item-sub {
  padding-left: 0.9rem;
}

.flyout-item-sub i {
  font-size: 1rem;
  min-width: 26px;
}

.flyout-fade-enter-active,
.flyout-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.flyout-fade-enter-from,
.flyout-fade-leave-to {
  opacity: 0;
  transform: scale(0.97) translateX(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .flyout-fade-enter-active,
  .flyout-fade-leave-active {
    transition: none;
  }
}

/* ======================== FOOTER ======================== */
.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--d-border);
}

.user-card {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.7rem;
  border: 1px solid var(--d-border);
  border-radius: 12px;
  background: rgba(96, 108, 56, 0.06);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid var(--d-border);
}

.user-avatar i {
  font-size: 1.3rem;
  color: var(--d-green);
}

.user-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.user-name {
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--d-green-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.72rem;
  color: var(--d-green);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-logout-btn {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--d-border);
  border-radius: 8px;
  background: transparent;
  color: var(--d-brown);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.user-logout-btn i {
  font-size: 1rem;
}

.user-logout-btn:hover {
  background: rgba(160, 34, 8, 0.1);
  color: #d32f2f;
  border-color: rgba(160, 34, 8, 0.3);
}

span {
  white-space: nowrap;
  overflow: hidden;
}
</style>
